import { logError } from '@savgroup-front-common/configuration/appInsights/AppInsights';
import {
  DYNAMIC_TERM_SUGGESTION_STATUS,
  DynamicTermSuggestionEntry,
  LOCAL_STORAGE_KEYS,
} from '@savgroup-front-common/types';

import {
  getFromLocalStorage,
  setToLocalStorage,
} from '../../helpers/localStorage';

import {
  TERM_SUGGESTION_BATCH_SIZE,
  TERM_SUGGESTION_TTL_IN_MILLISECONDS,
} from './dynamicTermSuggestion.configuration';
import {
  TERM_SUGGESTION_OUTCOME,
  TermSuggestionResult,
} from './dynamicTermSuggestion.types';

const entries = new Map<string, DynamicTermSuggestionEntry>();

let isHydrated = false;

const isExpired = ({
  entry,
  now,
}: {
  entry: DynamicTermSuggestionEntry;
  now: number;
}): boolean => now - entry.updatedAt > TERM_SUGGESTION_TTL_IN_MILLISECONDS;

const persist = (): void => {
  try {
    setToLocalStorage({
      key: LOCAL_STORAGE_KEYS.DYNAMIC_TERM_SUGGESTIONS,
      value: Object.fromEntries(entries),
    });
  } catch (error) {
    logError(error, {
      operation: 'Persist dynamic term suggestions',
    });
  }
};

export const sweepExpiredDynamicTerms = (): void => {
  const now = Date.now();

  const expiredMessageIds = [...entries.entries()]
    .filter(([, entry]) => isExpired({ entry, now }))
    .map(([messageId]) => messageId);

  if (!expiredMessageIds.length) {
    return;
  }

  expiredMessageIds.forEach((messageId) => entries.delete(messageId));

  persist();
};

const hydrate = (): void => {
  if (isHydrated) {
    return;
  }

  isHydrated = true;

  const storedEntries = getFromLocalStorage({
    key: LOCAL_STORAGE_KEYS.DYNAMIC_TERM_SUGGESTIONS,
    defaultValue: {},
  });

  const now = Date.now();

  Object.values(storedEntries ?? {})
    .filter((entry) => !!entry?.key && !isExpired({ entry, now }))
    .forEach((entry) => entries.set(entry.key, entry));
};

export const collectMissingDynamicTerm = ({
  messageId,
}: {
  messageId: string;
}): void => {
  hydrate();

  if (entries.has(messageId)) {
    return;
  }

  entries.set(messageId, {
    key: messageId,
    status: DYNAMIC_TERM_SUGGESTION_STATUS.PENDING,
    updatedAt: Date.now(),
  });
};

export const getPendingDynamicTermBatches = (): string[][] => {
  hydrate();

  const pendingKeys = [...entries.values()]
    .filter((entry) => entry.status === DYNAMIC_TERM_SUGGESTION_STATUS.PENDING)
    .map((entry) => entry.key);

  return pendingKeys.reduce<string[][]>((batches, key, index) => {
    if (index % TERM_SUGGESTION_BATCH_SIZE === 0) {
      return [...batches, [key]];
    }

    const previousBatches = batches.slice(0, -1);
    const currentBatch = batches.at(-1) ?? [];

    return [...previousBatches, [...currentBatch, key]];
  }, []);
};

const markAsSent = ({ keys }: { keys: string[] }): void => {
  const now = Date.now();

  keys
    .filter((key) => entries.has(key))
    .forEach((key) =>
      entries.set(key, {
        key,
        status: DYNAMIC_TERM_SUGGESTION_STATUS.SENT,
        updatedAt: now,
      }),
    );

  persist();
};

export const applyDynamicTermVerdicts = ({
  keys,
  results,
}: {
  keys: string[];
  results: TermSuggestionResult[];
}): void => {
  const acknowledgedKeys = results
    .filter(
      (result) =>
        result.outcome === TERM_SUGGESTION_OUTCOME.QUEUED ||
        result.outcome === TERM_SUGGESTION_OUTCOME.REFRESHED ||
        result.outcome === TERM_SUGGESTION_OUTCOME.SKIPPED,
    )
    .map((result) => keys[result.index] ?? result.key)
    .filter((key) => !!key);

  markAsSent({ keys: acknowledgedKeys });
};

export const discardDynamicTerms = ({ keys }: { keys: string[] }): void => {
  markAsSent({ keys });
};

export const persistDynamicTerms = (): void => persist();

export const resetDynamicTermSuggestionStoreForTests = (): void => {
  entries.clear();
  isHydrated = false;
};
